import { useState } from "react";
import { auth } from "../firebase";
import { C } from "../constants/colors";
import { TIER_DEFS, HAULER_QUESTIONS, OPP_DISPLAY, resolveOpportunity } from "../constants/domain";
import { sum, pct } from "../utils/format";
import { last12MonthsRecords, tonsOf } from "../utils/wasteAnalytics";
import { Card, Btn, SectionTag, ProgressBar, TriBtn, labelStyle, inputStyle } from "../components/ui";
import { Donut, MiniBar } from "../components/charts";

export default function AuditFlow({screen,setScreen,orgProfile,waste,dataConfigOrigin,setDataConfigOrigin}){
  const {
    org,setOrg,hauler,setHauler,baseline,setBaseline,
    locationAudits,activeLocationId,setActiveLocationId,
    actionPlan,setActionPlan,
    userConstraints,setUserConstraints,
    materials,baseQuestions,aggregatedAuditData,allCustomStreams,allMaterials,
    totalWeight,totalPhotos,hasAudit,segments,topMaterials,
    diversionRateNow,diversionRateTotal,
    saveNewAudit,addLocation,updateLocation,removeLocation,getActiveLoc,
    addCustomStream,updateCustomStream,removeCustomStream,
  }=orgProfile;
  const {wasteData,wasteDataConfig,setWasteDataConfig}=waste;

  const [loading,setLoading]=useState(false);
  const [loadingMsg,setLoadingMsg]=useState("");

  // ── Baseline & Hauler (always required) ───────────────────
  if(screen==="baseline") return (
    <div>
      <ProgressBar step={1} total={4}/>
      <SectionTag label="Step 1 — Hauler & Services"/>
      <h2 style={{fontSize:22,fontWeight:700,color:C.navy,margin:"12px 0 6px"}}>Hauler Profile & Active Services</h2>
      <p style={{color:C.muted,marginBottom:24,fontSize:14,lineHeight:1.6}}>These answers determine which materials are truly divertible at your location. They directly shape your results and action plan — and are required regardless of whether you conduct a physical audit today.</p>

      {/* Organization Info */}
      <Card style={{marginBottom:16,borderLeft:`4px solid ${C.teal}`}}>
        <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>Organization Info</div>
        <p style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>Required so the app knows which services and materials apply to your facility type.</p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px 24px"}}>
          <div>
            <div style={{fontSize:13,color:C.text,marginBottom:7}}>Industry</div>
            <select style={{...inputStyle,width:"100%"}} value={org.industry||""} onChange={e=>setOrg({...org,industry:e.target.value})}>
              <option value="">Select…</option>
              <option value="hospitality">Hospitality</option>
              <option value="healthcare">Healthcare</option>
            </select>
          </div>
          <div>
            <div style={{fontSize:13,color:C.text,marginBottom:7}}>Organization Size</div>
            <select style={{...inputStyle,width:"100%"}} value={org.size||""} onChange={e=>setOrg({...org,size:e.target.value})}>
              <option value="">Select…</option>
              <option value="small">Small (&lt;50 employees)</option>
              <option value="medium">Medium (50–200 employees)</option>
              <option value="large">Large (200+ employees)</option>
            </select>
          </div>
        </div>
        {!org.industry&&(
          <div style={{marginTop:14,background:C.orangeLight,borderRadius:8,padding:"10px 14px",fontSize:12,color:C.text}}>
            ⚠ Select your industry to unlock the "Active Services & Operations" questions below.
          </div>
        )}
      </Card>

      {/* Hauler Profile */}
      <Card style={{marginBottom:16,borderLeft:`4px solid ${C.navy}`}}>
        <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>Hauler Profile</div>
        <p style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>Your hauler's capabilities and your flexibility to change is the most important factor in determining what's actually divertible.</p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px 24px"}}>
          {HAULER_QUESTIONS.map(q=>(
            <div key={q.id} style={{marginBottom:4}}>
              <div style={{fontSize:13,color:C.text,marginBottom:7,lineHeight:1.4}}>{q.label}</div>
              {q.type==="text"
                ?<input style={{...inputStyle,fontSize:13}} placeholder={q.placeholder} value={hauler[q.id]||""} onChange={e=>setHauler({...hauler,[q.id]:e.target.value})}/>
                :<TriBtn options={q.opts} value={hauler[q.id]||""} onChange={v=>setHauler({...hauler,[q.id]:v})}/>
              }
            </div>
          ))}
        </div>
        {hauler.can_switch==="no"&&(
          <div style={{marginTop:14,background:C.orangeLight,borderRadius:8,padding:"10px 14px",fontSize:12,color:C.text}}>
            ⚠ With no hauler flexibility, diversion options are limited to source reduction, donation, and any specialty vendors you can independently contract. Your action plan will reflect this.
          </div>
        )}
      </Card>

      {/* Services & Operations */}
      <Card style={{marginBottom:24,borderLeft:`4px solid ${C.orange}`}}>
        <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>Active Services & Operations</div>
        <p style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>What's currently active at your location.</p>
        {!org.industry&&(
          <div style={{color:C.muted,fontSize:13,fontStyle:"italic"}}>Select your industry above to see the relevant questions.</div>
        )}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 24px"}}>
          {baseQuestions.map(q=>(
            <div key={q.id} style={{marginBottom:10}}>
              <div style={{fontSize:13,color:C.text,marginBottom:6,lineHeight:1.45}}>{q.label}</div>
              {q.type==="yesno"?(
                <div style={{display:"flex",gap:8}}>
                  {[["yes","✓ Yes"],["no","✗ No"]].map(([v,lbl])=>(
                    <button key={v} onClick={()=>setBaseline({...baseline,[q.id]:v})}
                      style={{padding:"5px 16px",borderRadius:20,border:`1px solid ${baseline[q.id]===v?C.teal:C.border}`,background:baseline[q.id]===v?C.tealLight:C.white,color:baseline[q.id]===v?C.teal:C.muted,fontWeight:600,fontSize:12,cursor:"pointer"}}>
                      {lbl}
                    </button>
                  ))}
                </div>
              ):q.type==="yesno3"?(
                <div style={{display:"flex",gap:6}}>
                  {[["yes","✓ Yes"],["sometimes","~ Sometimes"],["no","✗ No"]].map(([v,lbl])=>(
                    <button key={v} onClick={()=>setBaseline({...baseline,[q.id]:v})}
                      style={{padding:"5px 12px",borderRadius:20,border:`1px solid ${baseline[q.id]===v?C.teal:C.border}`,background:baseline[q.id]===v?C.tealLight:C.white,color:baseline[q.id]===v?C.teal:C.muted,fontWeight:600,fontSize:12,cursor:"pointer"}}>
                      {lbl}
                    </button>
                  ))}
                </div>
              ):(
                <select style={{...inputStyle,width:"auto",minWidth:180}} value={baseline[q.id]||""} onChange={e=>setBaseline({...baseline,[q.id]:e.target.value})}>
                  <option value="">Select…</option>
                  {q.options.map(o=><option key={o} value={o}>{o}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
      </Card>

      <div style={{display:"flex",justifyContent:"space-between"}}>
        <Btn variant="ghost" onClick={()=>setScreen("org-onboarding")}>← Back</Btn>
        <div style={{display:"flex",gap:10}}>
          <Btn variant="secondary" onClick={()=>setScreen("dashboard")}>Save & Go to Dashboard</Btn>
          <Btn onClick={()=>{setDataConfigOrigin("baseline");setScreen("data-config");}}>Continue →</Btn>
        </div>
      </div>
    </div>
  );

  // ── Waste Data Config (one-screen step after hauler/baseline setup) ───────
  if(screen==="data-config"){
    const volAnswer=wasteDataConfig.hasVolume===true?"cubic_yards":wasteDataConfig.hasVolume===false?"not_provided":"";
    const weightAnswer=wasteDataConfig.hasWeight===true?(wasteDataConfig.weightUnit||"tons"):wasteDataConfig.hasWeight===false?"not_provided":"";
    const setVolAnswer=v=>{
      const hasVolume=v==="cubic_yards";
      const next={...wasteDataConfig,hasVolume};
      next.unit=hasVolume?"cubic_yards":(next.hasWeight?next.weightUnit:next.unit);
      setWasteDataConfig(next);
    };
    const setWeightAnswer=v=>{
      const hasWeight=v==="tons"||v==="lbs";
      const next={...wasteDataConfig,hasWeight,weightUnit:hasWeight?v:wasteDataConfig.weightUnit};
      if(!next.hasVolume) next.unit=hasWeight?v:next.unit;
      setWasteDataConfig(next);
    };
    const canContinue=volAnswer!==""&&weightAnswer!=="";
    const backTarget=dataConfigOrigin==="hub"?"waste-hub":"baseline";
    const nextTarget=dataConfigOrigin==="hub"?"waste-hub":"audit-prep";
    return (
      <div>
        <SectionTag label="Data Setup"/>
        <h2 style={{fontSize:22,fontWeight:700,color:C.navy,margin:"12px 0 6px"}}>Waste Data Units</h2>
        <p style={{color:C.muted,marginBottom:24,fontSize:14,lineHeight:1.6}}>Tell us how your hauler reports volume and weight so the Waste Data Hub can chart your historical invoices correctly. If your hauler doesn't report one of these, that's fine — leave it as "Not Provided."</p>

        <Card style={{marginBottom:16}}>
          <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>Volume</div>
          <p style={{fontSize:13,color:C.muted,marginBottom:14,lineHeight:1.5}}>What unit does your hauler use for volume?</p>
          <TriBtn options={[["cubic_yards","Cubic Yards"],["not_provided","Not Provided"]]} value={volAnswer} onChange={setVolAnswer}/>
        </Card>

        <Card style={{marginBottom:24}}>
          <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>Weight</div>
          <p style={{fontSize:13,color:C.muted,marginBottom:14,lineHeight:1.5}}>What unit does your hauler use for weight?</p>
          <TriBtn options={[["tons","Tons"],["lbs","Lbs"],["not_provided","Not Provided"]]} value={weightAnswer} onChange={setWeightAnswer}/>
        </Card>

        <div style={{display:"flex",justifyContent:"space-between"}}>
          <Btn variant="ghost" onClick={()=>setScreen(backTarget)}>← Back</Btn>
          <div style={{display:"flex",gap:10}}>
            <Btn variant="secondary" onClick={()=>setScreen("dashboard")}>Save & Go to Dashboard</Btn>
            <Btn onClick={()=>setScreen(nextTarget)} disabled={!canContinue}>Continue →</Btn>
          </div>
        </div>
      </div>
    );
  }

  if(screen==="audit-prep"){
    const isH=org.industry==="hospitality";
    const steps=[
      {num:"01",title:"Walk your site first",icon:"🚶",body:"Before sorting a single bag, walk every area where waste is generated. Look at bin placement, signage, what's overflowing and what's empty. Talk to the people who handle waste daily.",tips:isH?["Check kitchen, back-of-house, guest floors, loading dock separately","Note where food waste mixes with dry waste","Look for cardboard baled vs. loose in dumpster"]:["Walk clinical areas, break rooms, nursing stations, and loading dock","Flag any RMW in wrong containers immediately","Check if recycling bins exist in break rooms and offices"]},
      {num:"02",title:"Pull representative bags",icon:"🗑️",body:"Select bags from your main landfill dumpster during a typical operational period. Aim for at least 50–100 lbs total for a reliable sample.",tips:isH?["Sample from kitchen, housekeeping, and F&B separately if possible","Avoid sorting during checkout rush or event days"]:["Do not open RMW bags","Break room and cafeteria bags are the most valuable to sort"]},
      {num:"03",title:"Set up your sorting station",icon:"🧤",body:"Lay a tarp on a flat surface. You'll need gloves, a scale, labels, and bins to sort into. Two people makes this significantly faster.",tips:["Weigh each sorted pile immediately","Use a bathroom scale: step on with pile, subtract your weight","Photograph sorted piles before weighing"]},
      {num:"04",title:"Sort by material category",icon:"♻️",body:"Empty each bag and sort contents into the material categories in the next step. Be consistent — pick the dominant material when in doubt.",tips:isH?["Food waste is usually the largest category","Paper cups are not recyclable — sort to landfill","Separate cardboard from mixed paper"]:["Break room trash often dominates","Medical supplies packaging is often specialty-recyclable","Paper towels are a major volume driver"]},
      {num:"05",title:"Note what you observe",icon:"📋",body:"Beyond weight data, document contamination, staff confusion at waste stations, and materials clearly divertible but going to landfill.",tips:["Note dumpster fill level — overfull = service misalignment","Check recycling bins for contamination","Ask staff: 'What do you wish was easier about sorting waste here?'"]},
    ];
    return (
      <div>
        <ProgressBar step={2} total={4}/>
        <SectionTag label="Step 2 — Audit Preparation"/>
        <h2 style={{fontSize:22,fontWeight:700,color:C.navy,margin:"12px 0 6px"}}>How to Conduct Your Waste Audit</h2>
        <p style={{color:C.muted,marginBottom:24,fontSize:14,lineHeight:1.65}}>Follow these steps before entering your data. A well-prepared sort produces reliable, actionable results.</p>
        <Card style={{background:C.navy,marginBottom:20}}>
          <div style={{fontWeight:700,color:C.teal,fontSize:12,letterSpacing:1,marginBottom:12}}>WHAT YOU'LL NEED</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            {[["🧤","Nitrile or rubber gloves"],["⚖️","Bathroom or hanging scale"],["🗂️","Tarp or flat surface"],["🏷️","Labels for each category"],["👥","1–2 people (2 is faster)"],["📱","Phone to record weights"]].map(([icon,lbl])=>(
              <div key={lbl} style={{display:"flex",alignItems:"center",gap:8}}><span style={{fontSize:18}}>{icon}</span><span style={{fontSize:12,color:"#c8d8e8",lineHeight:1.4}}>{lbl}</span></div>
            ))}
          </div>
        </Card>
        {steps.map((step,i)=>(
          <Card key={i} style={{marginBottom:14,borderLeft:`4px solid ${C.teal}`}}>
            <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>
              <div style={{minWidth:42,height:42,borderRadius:10,background:C.tealLight,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{step.icon}</div>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                  <span style={{fontSize:11,fontWeight:800,color:C.teal,letterSpacing:1}}>STEP {step.num}</span>
                  <span style={{fontWeight:700,color:C.navy,fontSize:15}}>{step.title}</span>
                </div>
                <p style={{fontSize:13,color:C.text,lineHeight:1.65,margin:"0 0 12px"}}>{step.body}</p>
                <div style={{background:C.sand,borderRadius:8,padding:"10px 14px"}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:6}}>{isH?"🏨 HOSPITALITY TIPS":"🏥 HEALTHCARE TIPS"}</div>
                  {step.tips.map((tip,j)=><div key={j} style={{fontSize:12,color:C.text,marginBottom:4,lineHeight:1.5}}><span style={{color:C.teal,fontWeight:700}}>→ </span>{tip}</div>)}
                </div>
              </div>
            </div>
          </Card>
        ))}
        {org.industry==="healthcare"&&(
          <Card style={{background:C.redLight,border:`1px solid ${C.red}33`,marginBottom:20}}>
            <div style={{fontWeight:700,color:C.red,fontSize:12,letterSpacing:1,marginBottom:8}}>⚠ HEALTHCARE SAFETY NOTE</div>
            <p style={{fontSize:13,color:C.text,lineHeight:1.65,margin:0}}>Never open or sort RMW bags, sharps containers, or biohazard containers. If you observe RMW in landfill bags, escalate immediately. Potential fines exceed $500,000/day in California.</p>
          </Card>
        )}
        <div style={{display:"flex",justifyContent:"space-between"}}>
          <Btn variant="ghost" onClick={()=>setScreen("baseline")}>← Back</Btn>
          <div style={{display:"flex",gap:10}}>
            <Btn variant="ghost" onClick={()=>setScreen("dashboard")}>Skip to Dashboard</Btn>
            <Btn onClick={()=>setScreen("audit")}>I'm Ready — Enter Audit Data →</Btn>
          </div>
        </div>
      </div>
    );
  }

  // ── Audit — Location Manager & Detail ─────────────────────
  if(screen==="audit"){
    const activeLoc=getActiveLoc();
    const photoAreas=org.industry==="hospitality"
      ?["Dumpster Area","Kitchen / Back-of-House","Guest Floors","Loading Dock","Recycling Station","Other"]
      :["Dumpster Area","Cafeteria / Kitchen","Break Rooms","Loading Dock","Clinical Areas","Recycling Station","Other"];

    // ── Location Detail view ─────────────────────────────────
    if(activeLoc){
      const locAllMaterials=[...materials,...(activeLoc.customStreams||[]).filter(s=>s.label.trim())];
      const locTotal=sum(Object.values(activeLoc.auditData||{}));
      const setLocData=(mid,val)=>updateLocation(activeLoc.id,{auditData:{...activeLoc.auditData,[mid]:val}});
      const addLocPhotos=async(files,area)=>{
        const newP=await Promise.all(Array.from(files).map(f=>new Promise(res=>{
          const r=new FileReader();r.onload=e=>res({url:e.target.result,label:area,name:f.name,date:new Date().toLocaleDateString()});r.readAsDataURL(f);
        })));
        updateLocation(activeLoc.id,{photos:[...(activeLoc.photos||[]),...newP]});
      };
      const removeLocPhoto=(photo)=>updateLocation(activeLoc.id,{photos:(activeLoc.photos||[]).filter(p=>p!==photo)});

      return (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
            <button onClick={()=>setActiveLocationId(null)}
              style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 14px",cursor:"pointer",color:C.muted,fontSize:13,fontWeight:600}}>
              ← All Locations
            </button>
            <div>
              <SectionTag label="Location Audit"/>
              <h2 style={{fontSize:20,fontWeight:800,color:C.navy,margin:"6px 0 0"}}>{activeLoc.name}</h2>
            </div>
          </div>

          {/* Rename */}
          <Card style={{marginBottom:16,padding:16}}>
            <label style={labelStyle}>Location Name</label>
            <input style={{...inputStyle,fontSize:14}} value={activeLoc.name}
              onChange={e=>updateLocation(activeLoc.id,{name:e.target.value})}/>
          </Card>

          {/* Material weights */}
          <Card style={{marginBottom:16}}>
            <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>Material Weights</div>
            <p style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>Enter weights in pounds for each category found in this location's sort. Leave at 0 if not observed here.</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 32px"}}>
              {materials.map(m=>(
                <div key={m.id} style={{marginBottom:14}}>
                  <div style={{marginBottom:4}}>
                    <span style={{fontSize:13,color:C.text,fontWeight:500}}>{m.label}</span>
                    {m.hint&&<div style={{fontSize:11,color:C.muted,marginTop:2,lineHeight:1.4}}>{m.hint}</div>}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <input type="number" min="0" value={activeLoc.auditData?.[m.id]||0}
                      onChange={e=>setLocData(m.id,parseFloat(e.target.value)||0)}
                      style={{width:90,padding:"6px 10px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:14,color:C.text,outline:"none"}}/>
                    <span style={{fontSize:13,color:C.muted}}>lbs</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{borderTop:`1px solid ${C.border}`,marginTop:8,paddingTop:12,fontSize:13,color:C.muted}}>
              Location total: <strong style={{color:C.navy}}>{locTotal.toFixed(1)} lbs</strong>
            </div>
          </Card>

          {/* Custom streams for this location */}
          <Card style={{marginBottom:16,borderLeft:`4px solid ${C.purple}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div>
                <div style={{fontWeight:700,color:C.navy,fontSize:14}}>Custom Streams</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2}}>Add streams specific to this location.</div>
              </div>
              <Btn variant="secondary" onClick={()=>addCustomStream(activeLoc.id)} style={{fontSize:12,padding:"6px 14px"}}>+ Add Stream</Btn>
            </div>
            {(activeLoc.customStreams||[]).length===0&&(
              <div style={{textAlign:"center",padding:"14px 0",color:C.muted,fontSize:13}}>No custom streams for this location.</div>
            )}
            {(activeLoc.customStreams||[]).map(s=>(
              <div key={s.id} style={{background:C.sand,borderRadius:10,padding:"14px 16px",marginBottom:10}}>
                <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12,marginBottom:10}}>
                  <div>
                    <label style={{...labelStyle,marginBottom:4}}>Stream Name</label>
                    <input style={{...inputStyle,fontSize:13}} placeholder="e.g. Ski Boot Packaging"
                      value={s.label} onChange={e=>updateCustomStream(activeLoc.id,s.id,"label",e.target.value)}/>
                  </div>
                  <div>
                    <label style={{...labelStyle,marginBottom:4}}>Weight (lbs)</label>
                    <input type="number" min="0" style={{...inputStyle,fontSize:13}}
                      value={activeLoc.auditData?.[s.id]||0}
                      onChange={e=>setLocData(s.id,parseFloat(e.target.value)||0)}/>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10}}>
                  <div>
                    <label style={{...labelStyle,marginBottom:4}}>Diversion Potential</label>
                    <select style={{...inputStyle,fontSize:12}} value={s.divertible} onChange={e=>updateCustomStream(activeLoc.id,s.id,"divertible",e.target.value)}>
                      <option value="none">No pathway currently</option>
                      <option value="compost">Compostable (needs composting service)</option>
                      <option value="cardboard">Cardboard collection</option>
                      <option value="paper">Mixed paper / recycling stream</option>
                      <option value="rigid_plastic">Rigid plastics (#1–#2) recycling</option>
                      <option value="aluminum">Aluminum recycling</option>
                      <option value="glass">Glass recycling (separate program)</option>
                      <option value="soft_plastics">Soft plastics — specialty vendor needed</option>
                      <option value="specialty_medical">Medical specialty vendor needed</option>
                      <option value="specialty_textile">Donate / textile reuse / specialty</option>
                      <option value="grease">Grease / cooking oil pickup</option>
                      <option value="self_haul">Self-haul or independent drop-off</option>
                      <option value="reduce">Source reduction only</option>
                      <option value="already_diverted">Already being diverted</option>
                    </select>
                  </div>
                  <div>
                    <label style={{...labelStyle,marginBottom:4}}>Notes (optional)</label>
                    <input style={{...inputStyle,fontSize:12}} placeholder="e.g. Vendor name, details"
                      value={s.hint||""} onChange={e=>updateCustomStream(activeLoc.id,s.id,"hint",e.target.value)}/>
                  </div>
                </div>
                <button onClick={()=>removeCustomStream(activeLoc.id,s.id)}
                  style={{fontSize:11,color:C.red,background:"none",border:"none",cursor:"pointer",fontWeight:600}}>
                  × Remove stream
                </button>
              </div>
            ))}
          </Card>

          {/* Photos for this location */}
          <Card style={{marginBottom:16}}>
            <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:4}}>Photos</div>
            <p style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>Capture dumpster fill levels, bin placement, contamination, and sorting conditions for this location.</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {photoAreas.map(area=>{
                const ap=(activeLoc.photos||[]).filter(p=>p.label===area);
                return(
                  <div key={area} style={{background:C.sand,borderRadius:10,padding:14}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                      <div style={{fontWeight:600,color:C.navy,fontSize:13}}>{area}</div>
                      <span style={{fontSize:11,color:C.muted}}>{ap.length} photo{ap.length!==1?"s":""}</span>
                    </div>
                    {ap.length>0&&(
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                        {ap.map((p,i)=>(
                          <div key={i} style={{position:"relative"}}>
                            <img src={p.url} alt={p.label} style={{width:56,height:56,objectFit:"cover",borderRadius:6,border:`1px solid ${C.border}`}}/>
                            <button onClick={()=>removeLocPhoto(p)}
                              style={{position:"absolute",top:-4,right:-4,width:16,height:16,borderRadius:"50%",background:C.red,color:C.white,border:"none",cursor:"pointer",fontSize:9,lineHeight:"16px",textAlign:"center",padding:0}}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <label style={{display:"block",padding:"6px 10px",borderRadius:7,border:`1px dashed ${C.teal}`,background:C.white,color:C.teal,fontSize:12,fontWeight:600,cursor:"pointer",textAlign:"center"}}>
                      + Add Photos<input type="file" accept="image/*" multiple style={{display:"none"}} onChange={e=>addLocPhotos(e.target.files,area)}/>
                    </label>
                  </div>
                );
              })}
            </div>
          </Card>

          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <button onClick={()=>setActiveLocationId(null)}
              style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 18px",cursor:"pointer",color:C.muted,fontSize:13,fontWeight:600}}>
              ← Back to All Locations
            </button>
            <Btn onClick={()=>setActiveLocationId(null)} disabled={locTotal===0}>Save Location →</Btn>
          </div>
        </div>
      );
    }

    // ── Location Manager (list view) ─────────────────────────
    return (
      <div>
        <ProgressBar step={3} total={4}/>
        <SectionTag label="Step 3 — Audit Locations"/>
        <h2 style={{fontSize:22,fontWeight:700,color:C.navy,margin:"12px 0 6px"}}>Audit Locations</h2>
        <p style={{color:C.muted,marginBottom:24,fontSize:14,lineHeight:1.6}}>
          Add each physical area or building you're auditing separately. Each location gets its own material weights, photos, and custom streams. Results are aggregated across all locations.
        </p>

        {locationAudits.length===0&&(
          <Card style={{textAlign:"center",padding:"40px 24px",marginBottom:20,border:`2px dashed ${C.border}`}}>
            <div style={{fontSize:32,marginBottom:12}}>📦</div>
            <div style={{fontWeight:700,color:C.navy,fontSize:16,marginBottom:8}}>No locations added yet</div>
            <p style={{color:C.muted,fontSize:13,marginBottom:20,lineHeight:1.6}}>
              Add one location to get started, or add multiple if you're auditing different buildings, wings, or dumpster zones separately.
            </p>
            <Btn onClick={()=>{const id=addLocation();setActiveLocationId(id);}}>+ Add First Location</Btn>
          </Card>
        )}

        {locationAudits.map((loc,i)=>{
          const locTotal=sum(Object.values(loc.auditData||{}));
          const locPhotos=(loc.photos||[]).length;
          const hasData=locTotal>0;
          return(
            <Card key={loc.id} style={{marginBottom:12,borderLeft:`4px solid ${hasData?C.green:C.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                    <span style={{fontSize:12,fontWeight:700,color:C.muted}}>#{i+1}</span>
                    <span style={{fontWeight:700,color:C.navy,fontSize:15}}>{loc.name}</span>
                    {hasData&&<span style={{fontSize:11,background:C.greenLight,color:C.green,fontWeight:700,padding:"2px 8px",borderRadius:10}}>✓ Data entered</span>}
                  </div>
                  <div style={{display:"flex",gap:16,fontSize:12,color:C.muted}}>
                    <span>⚖️ {locTotal.toFixed(1)} lbs sorted</span>
                    <span>📸 {locPhotos} photo{locPhotos!==1?"s":""}</span>
                    {(loc.customStreams||[]).filter(s=>s.label.trim()).length>0&&(
                      <span>➕ {loc.customStreams.filter(s=>s.label.trim()).length} custom stream{loc.customStreams.filter(s=>s.label.trim()).length!==1?"s":""}</span>
                    )}
                  </div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <Btn variant="secondary" onClick={()=>setActiveLocationId(loc.id)} style={{fontSize:12,padding:"6px 14px"}}>
                    {hasData?"Edit":"Enter Data →"}
                  </Btn>
                  <button onClick={()=>removeLocation(loc.id)}
                    style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 10px",cursor:"pointer",color:C.muted,fontSize:12,fontWeight:600}}>
                    ✕
                  </button>
                </div>
              </div>
            </Card>
          );
        })}

        {locationAudits.length>0&&(
          <button onClick={()=>{const id=addLocation();setActiveLocationId(id);}}
            style={{width:"100%",padding:"12px",border:`2px dashed ${C.teal}`,borderRadius:10,background:C.tealLight,color:C.teal,fontSize:13,fontWeight:600,cursor:"pointer",marginBottom:20}}>
            + Add Another Location
          </button>
        )}

        {locationAudits.length>0&&hasAudit&&(
          <Card style={{background:C.navy,marginBottom:20,padding:16}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:11,color:C.teal,fontWeight:700,marginBottom:4}}>LOCATIONS</div>
                <div style={{fontSize:22,fontWeight:800,color:C.white}}>{locationAudits.length}</div>
              </div>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:11,color:C.teal,fontWeight:700,marginBottom:4}}>TOTAL SORTED</div>
                <div style={{fontSize:22,fontWeight:800,color:C.white}}>{totalWeight.toFixed(0)} lbs</div>
              </div>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:11,color:C.teal,fontWeight:700,marginBottom:4}}>PHOTOS</div>
                <div style={{fontSize:22,fontWeight:800,color:C.white}}>{totalPhotos}</div>
              </div>
            </div>
          </Card>
        )}

        <div style={{display:"flex",justifyContent:"space-between"}}>
          <Btn variant="ghost" onClick={()=>setScreen("audit-prep")}>← Back</Btn>
          <div style={{display:"flex",gap:10}}>
            <Btn variant="secondary" onClick={()=>setScreen("dashboard")}>Save & Go to Dashboard</Btn>
            <Btn onClick={()=>setScreen("results")} disabled={!hasAudit}>Continue to Results →</Btn>
          </div>
        </div>
      </div>
    );
  }

  // ── Location Detail ────────────────────────────────────────
  if(screen==="location-detail"){
    const loc=getActiveLoc();
    if(!loc) return (
      <div style={{textAlign:"center",padding:"60px 0"}}>
        <p style={{color:C.muted}}>Location not found.</p>
        <Btn onClick={()=>setScreen("results")}>← Back to Results</Btn>
      </div>
    );

    const locMaterials=[...materials,...(loc.customStreams||[]).filter(s=>s.label.trim())];
    const locData=loc.auditData||{};
    const locTotal=sum(Object.values(locData));
    const locResolved=locMaterials.map(m=>({
      ...m,
      weight:locData[m.id]||0,
      resolved:resolveOpportunity(m.divertible,hauler,baseline),
    }));
    const locWithWeight=locResolved.filter(m=>m.weight>0).sort((a,b)=>b.weight-a.weight);
    const locDivertNow=sum(locResolved.filter(m=>m.resolved==="feasible_now").map(m=>m.weight));
    const locDivertVendor=sum(locResolved.filter(m=>m.resolved==="new_vendor").map(m=>m.weight));
    const locRateNow=pct(locDivertNow,locTotal);
    const locRateTotal=pct(locDivertNow+locDivertVendor,locTotal);

    // Donut segments for this location
    const locByResolved={};
    locResolved.forEach(m=>{if(m.weight>0){locByResolved[m.resolved]=(locByResolved[m.resolved]||0)+m.weight;}});
    const locSegments=Object.entries(locByResolved).map(([k,v])=>({
      label:OPP_DISPLAY[k]?.label||k,
      value:parseFloat(pct(v,locTotal)),
      color:OPP_DISPLAY[k]?.color||C.muted,
    })).sort((a,b)=>b.value-a.value);

    const locPhotos=loc.photos||[];

    // Find index for prev/next navigation
    const locIndex=locationAudits.findIndex(l=>l.id===loc.id);
    const prevLoc=locationAudits[locIndex-1]||null;
    const nextLoc=locationAudits[locIndex+1]||null;

    return (
      <div>
        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <button onClick={()=>setScreen("results")}
              style={{background:"none",border:"none",cursor:"pointer",color:C.muted,fontSize:13,fontWeight:600,padding:0,marginBottom:8,display:"flex",alignItems:"center",gap:4}}>
              ← Results Overview
            </button>
            <SectionTag label="Location Detail"/>
            <h2 style={{fontSize:22,fontWeight:800,color:C.navy,margin:"8px 0 4px"}}>{loc.name}</h2>
            <p style={{color:C.muted,fontSize:13}}>{org.name} · {org.industry} · {locTotal.toFixed(1)} lbs sorted</p>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {prevLoc&&(
              <button onClick={()=>setActiveLocationId(prevLoc.id)}
                style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${C.border}`,background:C.white,color:C.muted,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                ← {prevLoc.name}
              </button>
            )}
            {nextLoc&&(
              <button onClick={()=>setActiveLocationId(nextLoc.id)}
                style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${C.border}`,background:C.white,color:C.muted,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                {nextLoc.name} →
              </button>
            )}
            <Btn variant="secondary" onClick={()=>{setActiveLocationId(loc.id);setScreen("audit");}} style={{fontSize:12,padding:"7px 14px"}}>Edit Data</Btn>
          </div>
        </div>

        {/* Location tabs if multiple */}
        {locationAudits.length>1&&(
          <div style={{display:"flex",gap:4,marginBottom:20,flexWrap:"wrap"}}>
            {locationAudits.map((l,i)=>{
              const lTotal=sum(Object.values(l.auditData||{}));
              const isActive=l.id===loc.id;
              return(
                <button key={l.id} onClick={()=>setActiveLocationId(l.id)}
                  style={{padding:"7px 16px",borderRadius:20,border:`1px solid ${isActive?C.teal:C.border}`,
                    background:isActive?C.teal:C.white,color:isActive?C.white:C.muted,
                    fontSize:12,fontWeight:isActive?700:500,cursor:"pointer",transition:"all 0.15s"}}>
                  {l.name}
                  {lTotal>0&&<span style={{marginLeft:6,opacity:0.75}}>{lTotal.toFixed(0)} lbs</span>}
                </button>
              );
            })}
          </div>
        )}

        {locTotal===0?(
          <Card style={{textAlign:"center",padding:"40px 24px",border:`2px dashed ${C.border}`}}>
            <div style={{fontSize:28,marginBottom:10}}>⚖️</div>
            <div style={{fontWeight:700,color:C.navy,fontSize:15,marginBottom:8}}>No weight data for this location</div>
            <p style={{color:C.muted,fontSize:13,marginBottom:16}}>Enter material weights to see this location's breakdown.</p>
            <Btn onClick={()=>{setActiveLocationId(loc.id);setScreen("audit");}}>Enter Audit Data →</Btn>
          </Card>
        ):(
          <>
            {/* KPIs */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:20}}>
              {[
                {label:"Total Sorted",      value:`${locTotal.toFixed(0)} lbs`, color:C.navy  },
                {label:"Divertible Now",     value:`${locRateNow}%`,             color:C.green },
                {label:"With New Vendor",    value:`${locRateTotal}%`,           color:C.orange},
                {label:"Materials Found",    value:locWithWeight.length,         color:C.teal  },
              ].map(k=>(
                <Card key={k.label} style={{textAlign:"center",padding:14}}>
                  <div style={{fontSize:22,fontWeight:800,color:k.color}}>{k.value}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:4}}>{k.label}</div>
                </Card>
              ))}
            </div>

            {/* Charts */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
              <Card>
                <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:14}}>Composition by Stream</div>
                {locSegments.length>0?(
                  <div style={{display:"flex",alignItems:"center",gap:16}}>
                    <Donut segments={locSegments}/>
                    <div style={{flex:1}}>
                      {locSegments.map(s=>(
                        <div key={s.label} style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                          <div style={{width:10,height:10,borderRadius:2,background:s.color,flexShrink:0}}/>
                          <div style={{fontSize:11,color:C.muted,lineHeight:1.3}}>{s.label} <strong style={{color:C.navy}}>{s.value}%</strong></div>
                        </div>
                      ))}
                    </div>
                  </div>
                ):<p style={{color:C.muted,fontSize:13}}>No data.</p>}
              </Card>

              <Card>
                <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:12}}>All Materials — This Location</div>
                {locWithWeight.length===0?(
                  <p style={{color:C.muted,fontSize:13}}>No materials recorded.</p>
                ):locWithWeight.map(m=>(
                  <div key={m.id} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:3}}>
                      <span style={{fontSize:12,color:C.text,fontWeight:500}}>{m.label}</span>
                      <span style={{fontSize:12,color:C.muted,fontWeight:600}}>{m.weight.toFixed(1)} lbs · {pct(m.weight,locTotal)}%</span>
                    </div>
                    <div style={{height:5,background:C.border,borderRadius:3}}>
                      <div style={{height:5,borderRadius:3,background:OPP_DISPLAY[m.resolved]?.color||C.muted,width:`${pct(m.weight,locTotal)}%`,transition:"width 0.5s"}}/>
                    </div>
                  </div>
                ))}
              </Card>
            </div>

            {/* Full material table */}
            <Card style={{marginBottom:20}}>
              <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:14}}>Full Material Breakdown</div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:`2px solid ${C.border}`}}>
                    <th style={{textAlign:"left",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>MATERIAL</th>
                    <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>LBS</th>
                    <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>% OF LOCATION</th>
                    <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>% OF TOTAL AUDIT</th>
                    <th style={{textAlign:"left",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>STREAM STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {locWithWeight.map((m,i)=>{
                    const opp=OPP_DISPLAY[m.resolved];
                    return(
                      <tr key={m.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.sand}}>
                        <td style={{padding:"8px 10px",color:C.navy,fontWeight:500}}>{m.label}</td>
                        <td style={{padding:"8px 10px",textAlign:"right",color:C.text,fontWeight:600}}>{m.weight.toFixed(1)}</td>
                        <td style={{padding:"8px 10px",textAlign:"right",color:C.muted}}>{pct(m.weight,locTotal)}%</td>
                        <td style={{padding:"8px 10px",textAlign:"right",color:C.muted}}>{pct(m.weight,totalWeight)}%</td>
                        <td style={{padding:"8px 10px"}}>
                          {opp&&<span style={{fontSize:11,fontWeight:600,color:opp.color}}>{opp.icon} {opp.label}</span>}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{borderTop:`2px solid ${C.border}`,background:C.tealLight}}>
                    <td style={{padding:"8px 10px",fontWeight:700,color:C.navy}}>Location Total</td>
                    <td style={{padding:"8px 10px",textAlign:"right",fontWeight:700,color:C.navy}}>{locTotal.toFixed(1)}</td>
                    <td style={{padding:"8px 10px",textAlign:"right",fontWeight:700,color:C.navy}}>100%</td>
                    <td style={{padding:"8px 10px",textAlign:"right",fontWeight:700,color:C.navy}}>{pct(locTotal,totalWeight)}%</td>
                    <td/>
                  </tr>
                </tbody>
              </table>
            </Card>
          </>
        )}

        {/* Photos */}
        {locPhotos.length>0&&(
          <Card style={{marginBottom:20}}>
            <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:14}}>Photos — {loc.name} ({locPhotos.length})</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {locPhotos.map((p,i)=>(
                <div key={i} style={{textAlign:"center"}}>
                  <img src={p.url} alt={p.label} style={{width:100,height:100,objectFit:"cover",borderRadius:8,border:`1px solid ${C.border}`,display:"block"}}/>
                  <div style={{fontSize:10,color:C.muted,marginTop:4,maxWidth:100}}>{p.label}</div>
                </div>
              ))}
            </div>
          </Card>
        )}
        {locPhotos.length===0&&locTotal>0&&(
          <Card style={{marginBottom:20,border:`1px dashed ${C.border}`,textAlign:"center",padding:20}}>
            <div style={{color:C.muted,fontSize:13}}>No photos for this location. <button onClick={()=>{setActiveLocationId(loc.id);setScreen("audit");}} style={{color:C.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600,fontSize:13}}>Add photos →</button></div>
          </Card>
        )}

        <div style={{display:"flex",justifyContent:"space-between"}}>
          <Btn variant="ghost" onClick={()=>setScreen("results")}>← Results Overview</Btn>
          <Btn variant="secondary" onClick={()=>window.print()} style={{fontSize:12}}>Export PDF ↓</Btn>
        </div>
      </div>
    );
  }

  // ── Results ────────────────────────────────────────────────
  if(screen==="results") return (
    <div>
      <ProgressBar step={4} total={4}/>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
        <SectionTag label="Step 4 — Results"/>
        <Btn variant="ghost" onClick={()=>setScreen("dashboard")} style={{fontSize:11,padding:"5px 12px"}}>← Dashboard</Btn>
      </div>
      <h2 style={{fontSize:22,fontWeight:700,color:C.navy,margin:"12px 0 4px"}}>Waste Characterization Results</h2>
      <p style={{color:C.muted,marginBottom:24,fontSize:13}}>{org.name} · {totalWeight.toFixed(1)} lbs sorted</p>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:20}}>
        {[
          {label:"Total Sorted",       value:`${totalWeight.toFixed(0)} lbs`, color:C.navy  },
          {label:"Divertible Now",      value:`${diversionRateNow}%`,          color:C.green },
          {label:"With New Vendor",     value:`${diversionRateTotal}%`,        color:C.orange},
          {label:"Materials Found",     value:allMaterials.filter(m=>(aggregatedAuditData[m.id]||0)>0).length, color:C.teal},
        ].map(k=>(
          <Card key={k.label} style={{textAlign:"center",padding:14}}>
            <div style={{fontSize:22,fontWeight:800,color:k.color}}>{k.value}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>{k.label}</div>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
        <Card>
          <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:14}}>Composition by Opportunity</div>
          <div style={{display:"flex",alignItems:"center",gap:16}}>
            <Donut segments={segments}/>
            <div style={{flex:1}}>{segments.map(s=>(
              <div key={s.label} style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                <div style={{width:10,height:10,borderRadius:2,background:s.color,flexShrink:0}}/>
                <div style={{fontSize:11,color:C.muted,lineHeight:1.3}}>{s.label} <strong style={{color:C.navy}}>{s.value}%</strong></div>
              </div>
            ))}</div>
          </div>
        </Card>
        <Card>
          <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:12}}>Top Materials — Opportunity Tags</div>
          {topMaterials.map(m=>(
            <MiniBar key={m.id} label={m.label} pctVal={parseFloat(pct(m.weight,totalWeight))} color={OPP_DISPLAY[m.resolved]?.color||C.muted} tag={OPP_DISPLAY[m.resolved]}/>
          ))}
          {/* Legend */}
          <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${C.border}`,display:"flex",flexWrap:"wrap",gap:8}}>
            {[
              ["feasible_now","✅ Divertible Now"],
              ["new_vendor","🔄 Needs New Vendor"],
              ["linens_multi","♻️ Donate/Reduce/Specialty"],
              ["constrained","🚫 Constrained"],
              ["reduce","⬇️ Reduce at Source"],
              ["none","⬜ True Landfill"],
            ].map(([k,lbl])=>(
              <span key={k} style={{fontSize:10,color:OPP_DISPLAY[k]?.color||C.muted,fontWeight:600}}>{lbl}</span>
            ))}
          </div>
        </Card>
      </div>

      {/* Per-location breakdown */}
      {locationAudits.length>1&&(
        <Card style={{marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontWeight:700,color:C.navy,fontSize:14}}>Breakdown by Location</div>
            <Btn variant="secondary" onClick={()=>{if(!activeLocationId&&locationAudits.length>0)setActiveLocationId(locationAudits[0].id);setScreen("location-detail");}} style={{fontSize:12,padding:"6px 14px"}}>📍 View Location Details</Btn>
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{borderBottom:`2px solid ${C.border}`}}>
                  <th style={{textAlign:"left",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>LOCATION</th>
                  <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>LBS SORTED</th>
                  <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>% OF TOTAL</th>
                  <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>PHOTOS</th>
                  <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}></th>
                </tr>
              </thead>
              <tbody>
                {locationAudits.map((loc,i)=>{
                  const locTotal=sum(Object.values(loc.auditData||{}));
                  const locPct=totalWeight>0?((locTotal/totalWeight)*100).toFixed(1):"0.0";
                  return(
                    <tr key={loc.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.sand}}>
                      <td style={{padding:"8px 10px",fontWeight:600,color:C.navy}}>{loc.name}</td>
                      <td style={{padding:"8px 10px",textAlign:"right",color:C.text}}>{locTotal.toFixed(1)}</td>
                      <td style={{padding:"8px 10px",textAlign:"right",color:C.muted}}>{locPct}%</td>
                      <td style={{padding:"8px 10px",textAlign:"right",color:C.muted}}>{(loc.photos||[]).length}</td>
                      <td style={{padding:"8px 10px",textAlign:"right"}}>
                        <button onClick={()=>{setActiveLocationId(loc.id);setScreen("location-detail");}}
                          style={{fontSize:11,color:C.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600}}>View Details →</button>
                      </td>
                    </tr>
                  );
                })}
                <tr style={{borderTop:`2px solid ${C.border}`,background:C.tealLight}}>
                  <td style={{padding:"8px 10px",fontWeight:700,color:C.navy}}>Total</td>
                  <td style={{padding:"8px 10px",textAlign:"right",fontWeight:700,color:C.navy}}>{totalWeight.toFixed(1)}</td>
                  <td style={{padding:"8px 10px",textAlign:"right",fontWeight:700,color:C.navy}}>100%</td>
                  <td style={{padding:"8px 10px",textAlign:"right",fontWeight:700,color:C.navy}}>{totalPhotos}</td>
                  <td/>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Hauler constraint callout */}
      {hauler.can_switch==="no"&&(
        <Card style={{background:C.orangeLight,border:`1px solid ${C.orange}44`,marginBottom:16}}>
          <div style={{fontWeight:700,color:C.orange,fontSize:13,marginBottom:6}}>🔒 Hauler Constraint Active</div>
          <p style={{fontSize:13,color:C.text,margin:0,lineHeight:1.6}}>This location is locked into its current hauler. Diversion options are limited to what your hauler already offers, source reduction, independent specialty vendors you contract separately, and donation programs. The action plan below reflects these constraints.</p>
        </Card>
      )}

      {/* Overflow flag */}
      {(baseline.overflow==="yes"||baseline.overflow==="sometimes")&&(
        <Card style={{background:C.orangeLight,border:`1px solid ${C.orange}33`,marginBottom:16}}>
          <p style={{fontSize:13,color:C.text,margin:0}}>⚠ <strong>{baseline.overflow==="yes"?"Dumpster overflow detected.":"Occasional overflow noted."}</strong> {baseline.overflow==="yes"?"Service level mismatch — pickup frequency may need to increase or total volume must decrease before other interventions will be effective.":"Monitor fill levels. Even occasional overflow signals a service or volume misalignment worth addressing early."}</p>
        </Card>
      )}


      {/* Generate plan */}
      <Card>
        <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:14}}>Generate Your Action Plan</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:16}}>
          <div>
            <label style={labelStyle}>Timeline</label>
            <select style={inputStyle} value={userConstraints.timeline} onChange={e=>setUserConstraints({...userConstraints,timeline:e.target.value})}>
              <option value="30 days">30 Days</option><option value="6 months">6 Months</option>
              <option value="12 months">12 Months</option><option value="3 years">3 Years</option><option value="5 years">5 Years</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Budget</label>
            <select style={inputStyle} value={userConstraints.budget} onChange={e=>setUserConstraints({...userConstraints,budget:e.target.value})}>
              <option value="minimal">Minimal (no-cost only)</option><option value="moderate">Moderate</option><option value="flexible">Flexible (ROI-driven)</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Primary Goal</label>
            <select style={inputStyle} value={userConstraints.priority} onChange={e=>setUserConstraints({...userConstraints,priority:e.target.value})}>
              <option>Cost Reduction</option><option>Landfill Diversion</option><option>Regulatory Compliance</option><option>Staff Engagement</option>
            </select>
          </div>
        </div>
        <Btn onClick={async()=>{
          setLoading(true);setLoadingMsg("Generating action plan...");
          const matSummary=topMaterials.map(m=>`${m.label}: ${m.weight}lbs (${pct(m.weight,totalWeight)}%) [resolved:${m.resolved}]`).join("; ");
          const customSummary=allCustomStreams.filter(s=>(aggregatedAuditData[s.id]||0)>0)
            .map(s=>`${s.label}: ${aggregatedAuditData[s.id]||0}lbs [diversion: ${s.divertible}${s.hint?` — ${s.hint}`:""}]`).join("; ");
          const locationSummary=locationAudits.length>1
            ?locationAudits.map(l=>{const t=sum(Object.values(l.auditData||{}));return`${l.name}: ${t.toFixed(0)}lbs`;}).join(", ")
            :"";
          const records12mo=last12MonthsRecords(wasteData);
          let costSummary;
          if(records12mo.length===0){
            costSummary="No historical waste/cost data available.";
          }else{
            const totalCost=sum(records12mo.map(r=>r.costDollars||0));
            const totalTons=sum(records12mo.map(r=>tonsOf(r)||0));
            const byStream={};
            records12mo.forEach(r=>{ if(r.costDollars!=null){ byStream[r.stream]=(byStream[r.stream]||0)+r.costDollars; } });
            const streamBreakdown=Object.entries(byStream).sort((a,b)=>b[1]-a[1]).map(([s,c])=>`${s}: $${c.toLocaleString()}`).join(", ")||"n/a";
            costSummary=`Trailing 12 months: $${totalCost.toLocaleString()} total hauling cost across ${records12mo.length} billing record${records12mo.length!==1?"s":""}${totalTons>0?`, ${totalTons.toFixed(1)} tons hauled`:""}. By stream: ${streamBreakdown}.`;
          }
          const haulerSummary=`Hauler: ${hauler.hauler_name||"unknown"}. Can switch haulers: ${hauler.can_switch||"unknown"}. Hauler offers recycling: ${hauler.offers_recycling||"unknown"}. Hauler offers composting: ${hauler.offers_composting||"unknown"}. Hauler accepts glass separately: ${hauler.offers_glass||"unknown"}. Hauler accepts cardboard separately: ${hauler.offers_cardboard||"unknown"}. Hauler accepts soft plastics: ${hauler.offers_soft_plastic||"unknown"}.`;
          const bSummary=Object.entries(baseline).map(([k,v])=>`${k}:${v}`).join(", ");
          const tl=userConstraints.timeline;
          const activeTiers=TIER_DEFS[tl]||TIER_DEFS["12 months"];
          const tierKeys=activeTiers.map(t=>t.key);
          const sys=`You are a professional waste reduction consultant for hospitality and healthcare operations. Return ONLY valid JSON, no markdown, no code fences.

CRITICAL RULES — you must follow these exactly:
1. Each action must include a "feasibility" field: "feasible_now", "requires_new_vendor", or "reduce_at_source".
2. For "requires_new_vendor" actions, include a "vendor_note" field specifying the type of vendor needed.
3. NEVER recommend starting a mixed recycling, single-stream recycling, composting, or cardboard program unless the hauler data confirms it is available. If hauler offers_recycling is "no" or "unknown", do NOT recommend recycling stream activation as a simple step — flag it as requires_new_vendor.
4. Soft plastics (film, wrap, bags) CANNOT go in curbside recycling bins anywhere. Always recommend specialty programs (store drop-off, TerraCycle, specialty hauler) and mark as requires_new_vendor.
5. Glass is NOT accepted in most curbside single-stream programs in mountain/rural regions. Only recommend glass recycling if hauler offers_glass is "yes".
6. If hauler can_switch is "no", every recommendation involving a new service must be marked "requires_new_vendor" and note that a separate specialty vendor contract is required — not a hauler switch.
7. Actions must be specific, operational, and cost-framed — not aspirational or activist.

Mandatory content requirements:
- Every plan: at least 1 action on staff training (what to sort, where, why — operationally specific).
- Every plan: at least 1 action on signage and bin labeling.
- 6 months+: at least 1 action on internal communications strategy.
- 12 months+: at least 1 action on building a designated Waste Champion / Green Team.

Return this exact structure:
{
  "executive_summary":"3 sentences, operational, cost-focused — state the biggest opportunity and constraint",
  "estimated_diversion_gain":"e.g. 20-30%",
  "estimated_annual_savings":number (a dollar estimate grounded in the trailing-12-month cost data provided below; 0 if that data is unavailable or insufficient to ground an estimate),
  "kpis":["4 measurable KPI strings"],
  ${tierKeys.map(k=>`"${k}":[{"action":"string","why":"string","how":"string","resource":"string","impact":"high|medium|low","feasibility":"feasible_now|requires_new_vendor|reduce_at_source","vendor_note":"string or null"}]`).join(",\n  ")}
}
Each tier: exactly 4 actions.`;
          const prompt=`Organization: ${org.name}
Industry: ${org.industry}
Size: ${org.size}
${locationAudits.length>1?`Locations audited (${locationAudits.length}): ${locationSummary}\n`:""}
HAULER PROFILE:
${haulerSummary}

CURRENTLY ACTIVE SERVICES: ${bSummary}

TOP MATERIALS BY WEIGHT (with resolved diversion status):
${matSummary}
${customSummary?`\nCUSTOM STREAMS:\n${customSummary}`:""}

Total sorted: ${totalWeight}lbs
Divertible with current services: ${diversionRateNow}%
Divertible if new vendor added: ${diversionRateTotal}%

Historical waste & cost data: ${costSummary}
Timeline: ${tl} | Budget: ${userConstraints.budget} | Primary goal: ${userConstraints.priority}

Generate a realistic, operationally grounded action plan. Prioritize the highest-weight materials that are feasible now. Be explicit about what requires a new vendor vs. what can start immediately.`;
          try{
            const token=await auth.currentUser.getIdToken();
            const res=await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/claude`,{method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:4000,system:sys,messages:[{role:"user",content:prompt}]})});
            const data=await res.json();
            const clean=(data.content?.[0]?.text||"").replace(/```json|```/g,"").trim();
            const parsed=JSON.parse(clean);
            parsed._tiers=activeTiers;
            setActionPlan(parsed);setLoading(false);setScreen("plan");
            saveNewAudit({actionPlan:parsed});
          }catch(e){console.error(e);setActionPlan({error:true});setLoading(false);setScreen("plan");}
        }} disabled={loading} style={{width:"100%"}}>{loading?loadingMsg:"Generate Action Plan →"}</Btn>
      </Card>
    </div>
  );

  // ── Action Plan ────────────────────────────────────────────
  if(screen==="plan"){
    if(!actionPlan||actionPlan.error) return (
      <div style={{textAlign:"center",padding:"60px 0"}}>
        <div style={{fontSize:40,marginBottom:16}}>⚠️</div>
        <h2 style={{color:C.navy}}>Plan generation encountered an issue</h2>
        <p style={{color:C.muted}}>Go back and try again.</p>
        <Btn onClick={()=>setScreen("results")}>← Back to Results</Btn>
      </div>
    );
    const tiers=actionPlan._tiers||[{key:"now",label:"NOW",sub:"Weeks 1–4",col:C.green}];
    const feasibilityStyle={
      feasible_now:    {bg:C.greenLight,  color:C.green,  label:"✅ Feasible Now"},
      requires_new_vendor:{bg:C.orangeLight,color:C.orange, label:"🔄 Requires New Vendor"},
      reduce_at_source:{bg:C.tealLight,   color:C.teal,   label:"⬇️ Reduce at Source"},
    };
    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div><SectionTag label="Action Plan"/><h2 style={{fontSize:22,fontWeight:800,color:C.navy,margin:"10px 0 4px"}}>Waste Reduction Action Plan</h2><p style={{color:C.muted,fontSize:13}}>{org.name} · {org.industry} · {new Date().toLocaleDateString()}</p></div>
          <div style={{display:"flex",gap:8}}>
            <Btn variant="ghost" onClick={()=>setScreen("dashboard")} style={{fontSize:12,padding:"7px 14px"}}>← Dashboard</Btn>
            <Btn variant="secondary" onClick={()=>window.print()} style={{fontSize:12,padding:"7px 14px"}}>Export PDF ↓</Btn>
          </div>
        </div>
        <Card style={{background:C.navy,marginBottom:20}}>
          <div style={{fontSize:12,color:C.teal,fontWeight:700,letterSpacing:1,marginBottom:8}}>EXECUTIVE SUMMARY</div>
          <p style={{color:C.white,fontSize:14,lineHeight:1.7,margin:0}}>{actionPlan.executive_summary}</p>
          {actionPlan.estimated_diversion_gain&&<div style={{marginTop:12,display:"inline-block",background:C.teal+"33",color:"#c8e8f0",padding:"6px 14px",borderRadius:20,fontSize:12,fontWeight:600}}>Estimated diversion gain: {actionPlan.estimated_diversion_gain}</div>}
        </Card>
        {actionPlan.kpis?.length>0&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20}}>
            {actionPlan.kpis.map((k,i)=><div key={i} style={{background:C.sand,borderRadius:8,padding:"10px 14px",fontSize:12,color:C.text}}><span style={{fontWeight:700,color:C.teal}}>KPI {i+1}:</span> {k}</div>)}
          </div>
        )}
        {tiers.map(tier=>(
          <div key={tier.key} style={{marginBottom:24}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
              <div style={{background:tier.col,color:C.white,fontWeight:800,fontSize:12,padding:"4px 14px",borderRadius:20,letterSpacing:1}}>{tier.label}</div>
              <div style={{color:C.muted,fontSize:13}}>{tier.sub}</div>
            </div>
            {(actionPlan[tier.key]||[]).map((item,i)=>{
              const fs=feasibilityStyle[item.feasibility]||feasibilityStyle.feasible_now;
              return(
                <Card key={i} style={{marginBottom:10,borderLeft:`4px solid ${tier.col}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                    <div style={{fontWeight:700,color:C.navy,fontSize:15,flex:1,paddingRight:12}}>{item.action}</div>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                      <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:item.impact==="high"?C.greenLight:item.impact==="medium"?C.orangeLight:C.sand,color:item.impact==="high"?C.green:item.impact==="medium"?C.orange:C.muted}}>{(item.impact||"").toUpperCase()} IMPACT</span>
                      <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:fs.bg,color:fs.color}}>{fs.label}</span>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                    {[["WHY",item.why],["HOW",item.how],["RESOURCE",item.resource]].map(([hd,val])=>(
                      <div key={hd}><div style={{fontSize:10,fontWeight:700,color:C.muted,marginBottom:3,letterSpacing:0.5}}>{hd}</div><div style={{fontSize:12,color:C.text,lineHeight:1.5}}>{val}</div></div>
                    ))}
                  </div>
                  {item.vendor_note&&<div style={{marginTop:10,background:C.orangeLight,borderRadius:6,padding:"7px 12px",fontSize:11,color:C.orange}}><strong>Vendor needed:</strong> {item.vendor_note}</div>}
                </Card>
              );
            })}
          </div>
        ))}
        <Card style={{background:C.tealLight,border:`1px solid ${C.teal}44`,textAlign:"center",marginBottom:20}}>
          <div style={{fontWeight:700,color:C.navy,fontSize:15,marginBottom:6}}>Need expert support to implement this plan?</div>
          <div style={{color:C.muted,fontSize:13,marginBottom:14}}>Waste Free Tahoe provides hands-on implementation support, staff training, and ongoing tracking for hospitality and healthcare organizations.</div>
          <a href="https://wastefreetahoe.com" target="_blank" rel="noreferrer" style={{color:C.teal,fontWeight:600,fontSize:13,textDecoration:"none"}}>wastefreetahoe.com →</a>
        </Card>
        <style>{`@media print{button,nav{display:none;}}`}</style>
      </div>
    );
  }

  return null;
}
